import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Collapse, Modal, Row, Spin, Typography } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CheckSquareOutlined,
  ExclamationCircleFilled,
  HistoryOutlined,
  IdcardOutlined,
  ThunderboltOutlined
} from "@ant-design/icons";

import { useApi, ApiNames } from "../../hooks/api";
import { ActionButton } from "./taskStyles";
import { TaskStatePanel } from "./TaskStatePanel";
import { Routes } from "../../constant/routes";
import { ExpressEnrollMapper } from "../../component/Patient/ApplicationFormEditor/configurations";
import { ApplicationForm } from "../../component/Patient/ApplicationFormEditor/ApplicationForm";
import Sidebar from "../../component/Patient/ApplicationFormEditor/sidebar";
import { formSectionsController } from "../../component/Patient/ApplicationFormEditor/formSectionsController";
import { getFieldName } from "../../component/Patient/ApplicationFormEditor/utils/renderReactFromJson";
import { GRAY_200, GRAY_600, SUCCESS_500, WARNING_500 } from "../../constant/colors";

const { Text } = Typography;
const { Panel } = Collapse;

/** What the preview fetch settled on — see the effect in `EeReviewBody`. */
const LOAD_STATE = Object.freeze({
  LOADING: "loading",
  READY: "ready",
  /** 200 + null: the program has no active express-enrollment configuration. */
  NO_CONFIG: "no_config",
  ERROR: "error"
});

/**
 * The sidebar section holding a template's attestations. Templates name it
 * consistently (`consentFields` → `consent.*`, a `consent` sidebar item), and it
 * is the one split that changes what a navigator can DO: ticking a box needs
 * nobody, chasing a patient's email does. A template that mixed attestations
 * into another section would have them counted as details — visibly wrong rather
 * than silently, which is the failure mode to prefer.
 */
const ATTESTATION_SECTION_KEY = "consent";

/** Long enough for the window to be on screen before we scroll inside it. */
const MODAL_OPEN_MS = 300;

/** The application window's scroll container — what its sidebar anchors against. */
const FULL_FORM_SCROLL_ID = "agentic-ee-full-form-scroll";

/** `ApplicationForm` injects this into the store; it is not a field of the form. */
const SYNTHETIC_FIELDS = new Set(["form_disabled"]);

/** An antd `NamePath` as a stable key. */
const pathKey = (name) => (Array.isArray(name) ? name.join(".") : String(name));

/**
 * The form body for a template, whichever shape it declares. Templates come in
 * TWO: JSON-driven ones expose `getFormFromJson`, component-driven ones
 * `getFormSectionComponents`. The editor (`ApplicationFormEditor/modal.js`)
 * renders both, so this surface must too.
 *
 * Reading only one of them fails SILENTLY: `?.()` on the absent method yields
 * `undefined`, React renders nothing, and because `validateFields()` only
 * validates MOUNTED fields the summary then reports zero outstanding and unlocks
 * Approve on a blank application. That is exactly what happened when MVP-30690
 * converted `pfizer-copay-oral-iqvia` from components to JSON: the editor was
 * updated, this second consumer was not.
 *
 * Exported so the registry test can assert EVERY registered template still
 * renders through the real code path rather than a copy of this expression.
 */
export const resolveFormBody = (configuration, form) =>
  configuration?.getFormFromJson?.(form) ?? configuration?.getFormSectionComponents?.();

/**
 * `NamePath → the label the FORM shows`, derived by invoking each of the vendor
 * field bank's renderers once and reading the returned element's props.
 *
 * Labels only — no rendering, no second input surface. Naming a field for the
 * navigator has to match what they'll see when they open the application, and
 * humanising the path segment doesn't ("zip" → "Zip", where the form says "Zip
 * Code"). Every renderer is invoked unconditionally in stable order: some resolve
 * labels through hooks, and a conditional subset would break the rules of hooks.
 */
const useFieldLabels = (templateName) => {
  const vendor = templateName?.replace(/-/g, "_");
  const getSectionFields = formSectionsController().getSectionFieldsByVendor(vendor);
  const sections = getSectionFields ? getSectionFields() : null;

  const labels = new Map();
  Object.values(sections || {}).forEach((fields) => {
    Object.values(fields || {}).forEach((render) => {
      if (typeof render !== "function") return;
      let element;
      try {
        element = render({});
      } catch {
        return; // needs args we can't supply — the path fallback covers it
      }
      const name = element?.props?.name;
      if (name && element.props.label) labels.set(pathKey(name), element.props.label);
    });
  });
  if (labels.size) return labels;

  // JSON-driven template: it has no vendor field bank to invoke, so read the
  // labels off the form definition instead. Deliberately AFTER the block above
  // rather than as an early branch — for a JSON template `getSectionFields` is
  // undefined, so that loop invokes no renderers and therefore no hooks, and the
  // hook order the comment above protects stays identical either way.
  const mapped = ExpressEnrollMapper[templateName];
  const configuration = typeof mapped === "function" ? mapped() : mapped;
  (configuration?.formJson?.formSections || []).forEach((section) =>
    (section.rows || []).forEach((row) =>
      (row.fields || []).forEach((field) => {
        // `getFieldName` owns the subSection nesting (`patient.caregiver.dob`),
        // so the keys match the paths validation reports errors on.
        if (field.label) labels.set(pathKey(getFieldName(section.sectionName, field)), field.label);
      })
    )
  );
  return labels;
};

/**
 * The `ee_review` modal body — the navigator's express-enrollment review.
 *
 * Framing: the agent did the work, the navigator checks it and closes what's
 * left. So this surface REPORTS — what was prepared, what is outstanding, how it
 * got here — and the application itself opens in its own window for the actual
 * editing.
 *
 * ONE place to edit, deliberately. An earlier revision also rendered the missing
 * fields as editable rows here, which meant mounting the same field twice; every
 * bug in this modal so far came from that seam (a row going green while empty,
 * a checkbox printing `true`). The form is the single surface now, with its own
 * validation and its own sidebar.
 *
 * Values are fetched LIVE on every open rather than carried on the task, so a
 * review that sat for days never shows stale patient data — and they never touch
 * the agentic platform: this fetch is FNC↔FN, and only ids go back to the
 * workflow.
 */
const EeReviewBody = ({ papId, patientId, form, onReady, onErrors, summary = null, summaryStepCount = 0 }) => {
  const { t } = useTranslation();
  const [loadState, setLoadState] = useState(LOAD_STATE.LOADING);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [fetchPreview, preview] = useApi(ApiNames.AgenticEeReviewPreview);

  // Three outcomes, kept distinct on purpose: FN answers 200 with `null` when the
  // program has no active express-enrollment config (an ABSENT preview, not a
  // failed call), and `useApi`'s invoke never rejects — it RESOLVES `undefined`
  // when the request fails. Reading only the tuple's `preview` would collapse
  // all three into "still null" and spin forever.
  //
  // `useApi` hands back a fresh function identity each render, so the effect keys
  // on `papId` alone — adding the fetcher would re-request on every render.
  useEffect(() => {
    if (papId == null) return;
    setLoadState(LOAD_STATE.LOADING);
    fetchPreview({ papId }).then((result) => {
      if (result === undefined) return setLoadState(LOAD_STATE.ERROR);
      return setLoadState(result ? LOAD_STATE.READY : LOAD_STATE.NO_CONFIG);
    });
  }, [papId]);

  const hasUsablePreview = loadState === LOAD_STATE.READY && Boolean(preview?.templateName);

  useEffect(() => {
    if (hasUsablePreview) onReady?.(preview);
  }, [hasUsablePreview, preview]);

  // The form's own validation is the source of truth for what's outstanding —
  // required-ness is decided at render time by the template's components, so
  // nothing else can know it. Kept here for the summary, and forwarded up so the
  // modal footer gates Approve on exactly the same set.
  //
  // DEDUPED, and that is load-bearing rather than an optimisation. `ApplicationForm`
  // reports errors unconditionally from `onFieldsChange` and again after each
  // `validateFields()`, always as a NEW array, and it re-runs
  // `resetFields`/`validateFields` whenever `JSON.stringify(initialValues)`
  // differs — while `initialValues` is recomputed during render. So a report that
  // sets state re-renders, which can produce a different key, which validates
  // again: the express-enrollment review modal froze the tab on open, with
  // nothing in the console because nothing threw.
  //
  // Compared by the FIELD NAMES that are failing, which is all any consumer uses:
  // the footer gates on `length`, the prepared card lists the outstanding paths,
  // and the sidebar counts them per root. An unchanged set reports nothing, so
  // the cycle cannot close.
  const lastErrorKeyRef = useRef(null);
  const handleErrors = (errors) => {
    const next = errors ?? [];
    const key = next
      .map(({ name }) => (Array.isArray(name) ? name.join(".") : String(name)))
      .sort()
      .join("|");
    if (key === lastErrorKeyRef.current) return;
    lastErrorKeyRef.current = key;
    setFieldErrors(next);
    onErrors?.(next);
  };

  if (loadState === LOAD_STATE.ERROR) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("agentic_board.ee_review.load_failed")}
        data-testid="agentic-ee-review-load-error"
      />
    );
  }

  // Not an error — this program simply isn't express-enroll enabled right now,
  // so there is nothing for the navigator to approve.
  if (loadState === LOAD_STATE.NO_CONFIG) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t("agentic_board.ee_review.no_config")}
        data-testid="agentic-ee-review-no-config"
      />
    );
  }

  if (!hasUsablePreview) {
    return (
      <div style={{ textAlign: "center", padding: 32 }} data-testid="agentic-ee-review-loading">
        <Spin />
      </div>
    );
  }

  return (
    <div data-testid="agentic-ee-review-body">
      {/* The form below carries its own `0 1.5em` gutter (ExpressEnrollmentStyledForm,
          shared with the real enroll tab). Match it here rather than override it
          there, so every block in this modal lines up on the same edges. */}
      <div style={{ padding: "0 1.5em" }}>
        {/* Opens exactly as the consent task does — the shared panel, not a
            lookalike. A navigator moving between the two should feel one product,
            and green because this IS good news: the agent already did the work. */}
        <div style={{ marginBottom: 12 }}>
          <TaskStatePanel
            icon={ThunderboltOutlined}
            title={t("agentic_board.ee_review.intro_title")}
            body={t("agentic_board.ee_review.intro")}
            goodNews
            data-testid="agentic-ee-review-intro"
          />
        </div>

        {summary && (
          // Context, not the task. Collapsed by default so the state of the
          // enrollment is what the navigator meets first.
          // Carries the same border and radius as the cards around it: a bare
          // chevron and a label was the one element in this modal with no shape,
          // which read as an artifact rather than something to click. The step
          // count keeps it from being mute while closed.
          <Collapse
            ghost
            style={{ marginBottom: 12, border: `1px solid ${GRAY_200}`, borderRadius: 8 }}
            data-testid="agentic-ee-review-summary"
          >
            <Panel
              key="summary"
              header={
                <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <HistoryOutlined style={{ fontSize: 15, color: GRAY_600 }} />
                  <span style={{ fontSize: 13 }}>{t("agentic_board.ee_review.discovery_summary")}</span>
                  {summaryStepCount > 0 && (
                    <Text style={{ fontSize: 12, color: GRAY_600 }}>
                      {t("agentic_board.ee_review.discovery_steps", { count: summaryStepCount })}
                    </Text>
                  )}
                </span>
              }
            >
              {summary}
            </Panel>
          </Collapse>
        )}

        <PreparedCard form={form} fieldErrors={fieldErrors} templateName={preview.templateName} t={t} />
      </div>

      {/* Keyed by template so a different program remounts rather than reordering
          the template config's own hooks. */}
      <EeReviewForm
        key={preview.templateName}
        templateName={preview.templateName}
        formData={preview.formData}
        form={form}
        patientId={patientId}
        enrollmentName={
          preview.programName ? `${preview.programName}${preview.drugName ? ` (${preview.drugName})` : ""}` : null
        }
        fieldErrors={fieldErrors}
        onErrors={handleErrors}
      />
    </div>
  );
};

/**
 * What the agent produced, and what is left — the one claim this surface makes.
 *
 * Counts come from the MOUNTED FORM (`getFieldsValue`), never the backend: FN can
 * say which fields it autofilled, but only the form knows how many fields exist
 * and which are still empty. Outstanding fields are NAMED in the sentence rather
 * than listed, because at one or two the name is the useful part; past a few it
 * counts the rest instead of sprawling.
 */
const PreparedCard = ({ form, fieldErrors, templateName, t }) => {
  // ONE set behind both numbers, or the sentence contradicts itself. Counting
  // the store separately doesn't work: an unchecked consent box has no value in
  // it at all, so it would be missing from the total while still being reported
  // as outstanding ("15 of 16 filled. 4 need you").
  //
  // So: outstanding is the deduped error set, filled is every real value that
  // ISN'T outstanding (an invalid entry is not "filled"), and the total is their
  // sum. It can only ever add up.
  const outstanding = [...new Set(fieldErrors.map(({ name }) => pathKey(name)))];
  const isOutstanding = new Set(outstanding);
  const filled = flattenValues(form.getFieldsValue(true)).filter(
    ([path, value]) => !SYNTHETIC_FIELDS.has(path) && isFilled(value) && !isOutstanding.has(path)
  ).length;
  const total = filled + outstanding.length;
  const isReady = outstanding.length === 0;

  // Split the outstanding work by what it asks of the navigator. Attestations are
  // theirs to give; details have to be found. Named by the label the FORM uses,
  // falling back to the path's last segment when a renderer doesn't expose one.
  const labels = useFieldLabels(templateName);
  const grouped = outstanding.reduce(
    (groups, path) => {
      if (path.split(".")[0] === ATTESTATION_SECTION_KEY) return { ...groups, attestations: groups.attestations + 1 };
      const segments = path.split(".");
      const fallback = segments[segments.length - 1].replace(/([A-Z])/g, " $1");
      const label = labels.get(path) || fallback.charAt(0).toUpperCase() + fallback.slice(1);
      return { ...groups, details: [...groups.details, label] };
    },
    { attestations: 0, details: [] }
  );

  return (
    <div
      style={{
        background: "rgba(0,0,0,0.02)",
        border: `1px solid ${GRAY_200}`,
        borderRadius: 8,
        padding: "12px 14px",
        marginBottom: 12
      }}
      data-testid="agentic-ee-review-prepared"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {isReady ? (
          <CheckCircleFilled style={{ color: SUCCESS_500, fontSize: 16 }} />
        ) : (
          <ThunderboltOutlined style={{ color: SUCCESS_500, fontSize: 16 }} />
        )}
        <Text strong>
          {t(isReady ? "agentic_board.ee_review.ready_title" : "agentic_board.ee_review.prepared_title")}
        </Text>
      </div>

      {/* The form mounts a render after this card, so the store is empty on the
          first paint — "0 of 0 fields" would be a worse first impression than
          saying nothing for one frame. */}
      {total > 0 && (
        <Text style={{ display: "block", marginTop: 6, color: GRAY_600, fontSize: 13, lineHeight: 1.6 }}>
          {isReady
            ? t("agentic_board.ee_review.ready_body", { total })
            : t("agentic_board.ee_review.prepared_body", { filled, total })}
        </Text>
      )}

      {total > 0 && (
        <div
          style={{
            display: "flex",
            height: 4,
            borderRadius: 2,
            overflow: "hidden",
            marginTop: 10,
            background: GRAY_200
          }}
        >
          {/* Complete is complete: one full green bar, never a hairline of amber
              left over from arithmetic. */}
          <div style={{ flex: filled, background: SUCCESS_500 }} />
          {!isReady && <div style={{ flex: outstanding.length, background: WARNING_500 }} />}
        </div>
      )}

      {/* What the outstanding work ASKS OF YOU — the split that decides whether a
          navigator can finish now or has to go and find something. */}
      {!isReady && (
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 12 }}>
          {grouped.attestations > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="agentic-ee-attestations">
              <CheckSquareOutlined style={{ color: WARNING_500, fontSize: 15, flexShrink: 0 }} />
              <Text style={{ fontSize: 13 }}>
                {t("agentic_board.ee_review.attestations", { count: grouped.attestations })}
              </Text>
            </div>
          )}
          {grouped.details.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="agentic-ee-details">
              {/* antd 4 has no person-with-magnifier; an ID card is the nearest
                  mark for "particulars we have to go and find". */}
              <IdcardOutlined style={{ color: WARNING_500, fontSize: 15, flexShrink: 0 }} />
              <Text style={{ fontSize: 13 }}>
                {t("agentic_board.ee_review.details_missing", { count: grouped.details.length })}
              </Text>
              <Text style={{ fontSize: 12, color: GRAY_600 }} ellipsis>
                {grouped.details.join(", ")}
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Mounts the template's own express-enrollment form — the per-section state line
 * and the window the form itself lives in.
 *
 * Separate component ON PURPOSE: a registry entry may be a CONFIG HOOK
 * (`() => usePfizerCopayOralIQVIAConfiguration()`), so resolving it has to happen
 * at a component's top level — never inside a `useMemo`, which would call a hook
 * conditionally. The parent keys this by `templateName`, so switching programs
 * remounts instead of changing hook order mid-life.
 */
const EeReviewForm = ({ templateName, formData, form, patientId, enrollmentName, fieldErrors, onErrors }) => {
  const { t } = useTranslation();
  const [fullFormOpen, setFullFormOpen] = useState(false);
  const mapped = ExpressEnrollMapper[templateName];
  const configuration = typeof mapped === "function" ? mapped() : mapped;

  const formBody = resolveFormBody(configuration, form);

  // A template with no registered form components can't be reviewed here. The
  // agent's program setting is meant to prevent this reaching a navigator, so say
  // so plainly rather than render an empty form. `formBody` is part of the test:
  // a config that renders NEITHER shape must fail loudly here, not blank later.
  if (!configuration || !formBody) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t("agentic_board.ee_review.unsupported_template", { template: templateName })}
        data-testid="agentic-ee-review-unsupported"
      />
    );
  }

  const initialValues = configuration.getFormInitialValues
    ? configuration.getFormInitialValues({ applicationForm: { formData } })
    : formData;

  // The template's own sidebar — a FUNCTION, rebuilt per render because a
  // section's visibility can depend on live values (the caregiver section
  // vanishes for an adult). Same call the enroll tab makes in `modal.js`.
  const sidebarItems = configuration.getSidebarItems?.(t, undefined, form) || [];

  // A section's outstanding count: failing fields grouped by ROOT path segment,
  // matched against that section's `validationFields` — the enroll tab's own
  // derivation, carried one step further to a number.
  const errorsPerRoot = (fieldErrors || []).reduce((counts, { name }) => {
    const root = Array.isArray(name) ? name[0] : name;
    return { ...counts, [root]: (counts[root] || 0) + 1 };
  }, {});
  const sectionState = sidebarItems.map((item) => {
    const fields = item.validationFields || [];
    return {
      key: item.key,
      title: item.title,
      remaining: fields.reduce((sum, field) => sum + (errorsPerRoot[field] || 0), 0)
    };
  });

  // Opening at the top leaves the navigator hunting for the work. The anchors and
  // the failing set are both in hand, so land on the first one instead — after the
  // window is actually visible, since antd can't measure a hidden target.
  const openApplication = () => {
    setFullFormOpen(true);
    const firstGap = fieldErrors?.[0]?.name;
    if (!firstGap) return;
    setTimeout(() => form.scrollToField(firstGap, { behavior: "smooth", block: "center" }), MODAL_OPEN_MS);
  };

  return (
    <>
      {/* Where the work stands, section by section, and the one way in. The
          sections come from the template, so this costs nothing per program. */}
      <div
        style={{
          // The gutter the cards above sit on. It used to come free from
          // `ExpressEnrollmentStyledForm`, back when the form wrapped this row;
          // the form now lives inside the window where it belongs, so this row
          // carries the gutter itself.
          margin: "0 1.5em",
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          border: `1px solid ${GRAY_200}`,
          borderRadius: 8,
          padding: "4px 12px",
          marginBottom: 4
        }}
        data-testid="agentic-ee-review-sections"
      >
        {sectionState.map(({ key, title, remaining }) =>
          // A tick that means "incomplete" reads as done at a glance — an
          // outstanding section gets its own mark AND its count, so the navigator
          // can see where the work actually sits before opening anything.
          remaining > 0 ? (
            <Text key={key} style={{ fontSize: 12 }}>
              <ExclamationCircleFilled style={{ color: WARNING_500, marginRight: 5 }} />
              {title} · {t("agentic_board.ee_review.section_remaining", { count: remaining })}
            </Text>
          ) : (
            <Text key={key} style={{ fontSize: 12 }}>
              <CheckCircleFilled style={{ color: SUCCESS_500, marginRight: 5 }} />
              {title}
            </Text>
          )
        )}
        {/* The hub's own action pill — soft-tinted primary, 8px radius — so this
            reads as the same family of action as the modal's footer buttons
            rather than a default antd button dropped into the row. */}
        {/* `ActionButton` forces its own padding and weight through an `&&`, so
            `size="small"` alone only lowers the height — beside 12px chips it
            still reads as a footer-scale pill. Pin the metrics to this row. */}
        <ActionButton
          size="small"
          style={{ marginLeft: "auto", height: 28, padding: "0 10px", fontSize: 12, lineHeight: "26px" }}
          onClick={openApplication}
          data-testid="agentic-ee-review-open-full-form"
        >
          {t("agentic_board.ee_review.full_application")} <ArrowRightOutlined style={{ fontSize: 11 }} />
        </ActionButton>
      </div>

      {/*
        The application, in the window it was designed for — a SEPARATE window,
        but NOT a separate form: the `form` instance is created by the parent and
        handed to `ApplicationForm` below, so the summary out here and the fields
        in there are the same store. Nothing is handed over on open, nothing is
        saved on close.

        `ApplicationForm` sits INSIDE this Modal, mirroring `modal.js`, and that
        placement is load-bearing. A Modal portals its DOM to `document.body`;
        with the form outside, every field rendered in here landed outside the
        `<form class="ant-form-vertical">` element, and antd's vertical-layout
        rules — labels above the control, no colon, trailing required mark — are
        plain descendant CSS keyed on that class, as is every rule in
        `ExpressEnrollmentStyledForm`. React context still crossed the portal, so
        the fields bound correctly and only the LOOK broke: labels snapped inline
        with colons and the editor's error styling vanished. Keep the form inside
        the window it renders in.

        `forceRender` is load-bearing too: it keeps the sections MOUNTED while the
        window is closed. `validateFields()` only validates mounted fields, so
        without it the summary would report zero outstanding and Approve would
        unlock with required fields still blank.
      */}
      <Modal
        open={fullFormOpen}
        onCancel={() => setFullFormOpen(false)}
        // The editor's own dimensions (`modal.js`): a percentage, not a fixed
        // 1100px. The template lays its fields out in 3-4 column ConfigurableRows
        // that only breathe at that width — pinned to 1100 they wrap and the
        // labels stop lining up.
        width="85%"
        centered
        forceRender
        destroyOnClose={false}
        title={
          // Same identity line as the task modal behind it: the window can cover
          // that title completely, so it has to say which enrollment and whose.
          <>
            {t("agentic_board.ee_review.full_application")}
            {enrollmentName && (
              <Text style={{ display: "block", fontSize: 12, fontWeight: 400, color: GRAY_600 }}>{enrollmentName}</Text>
            )}
            {patientId != null && (
              <Link
                to={Routes.PATIENT_PATH({ patientId })}
                style={{ fontSize: 12, fontWeight: 400 }}
                data-testid="agentic-ee-full-form-patient-link"
              >
                {t("agentic_board.modal_patient", { patientId })}
              </Link>
            )}
          </>
        }
        footer={
          // Same gate as Approve: an invalid form can't be dismissed as finished.
          // The X still closes — that's "leave it for now", not "done".
          <Button
            type="primary"
            disabled={fieldErrors.length > 0}
            onClick={() => setFullFormOpen(false)}
            data-testid="agentic-ee-full-form-done"
          >
            {t("agentic_board.ee_review.full_application_done")}
          </Button>
        }
        data-testid="agentic-ee-review-full-form"
      >
        {/* The editor's own two-pane layout: its `Sidebar`, driven by the
            template's sidebar items, beside the sections. */}
        {/* The editor's own two-pane markup (`modal.js`): a fixed-width rail with
            a right border, top-aligned. No `position: sticky` — `Anchor` affixes
            itself by default, and stacking sticky on top of that is what pushed
            the rail into the middle of the pane. */}
        <div id={FULL_FORM_SCROLL_ID} style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
          {/* The editor's exact two-pane markup (`modal.js`): antd Row/Col, not
              plain flex. The template's field rows are `ConfigurableRow`s — antd
              Rows with gutters — and they only lay out as designed inside an antd
              Col; a bare flex parent leaves controls of differing heights (a
              Select beside Inputs) sitting on different baselines. */}
          <Row wrap={false} style={{ minHeight: "75vh" }}>
            {sidebarItems.length > 0 && (
              <Col
                flex="none"
                style={{ paddingRight: "0.5em", paddingTop: "1em", borderRight: `1px solid ${GRAY_200}`, width: 200 }}
              >
                <Sidebar
                  sidebarItems={sidebarItems}
                  sectionsWithErrors={sectionState.filter(({ remaining }) => remaining > 0).map(({ key }) => key)}
                  activeFormSection={configuration.activeFormSection}
                  formId="agentic-ee-review-form"
                  getContainer={() => document.getElementById(FULL_FORM_SCROLL_ID)}
                />
              </Col>
            )}
            <Col flex="auto" style={{ minWidth: 0 }}>
              <ApplicationForm
                form={form}
                formId="agentic-ee-review-form"
                initialValues={initialValues}
                isFormDisabled={false}
                onErrors={onErrors}
              >
                {formBody}
              </ApplicationForm>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

const isFilled = (value) =>
  value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && !value.length);

/** `{a: {b: 1}}` → `[["a.b", 1]]`, so field counts don't miss nested sections. */
const flattenValues = (value, prefix = "") =>
  Object.entries(value || {}).flatMap(([key, entry]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const isPlainObject = entry && typeof entry === "object" && !Array.isArray(entry) && !entry._isAMomentObject;
    return isPlainObject ? flattenValues(entry, path) : [[path, entry]];
  });

EeReviewForm.propTypes = {
  templateName: PropTypes.string.isRequired,
  patientId: PropTypes.number,
  enrollmentName: PropTypes.string,
  formData: PropTypes.object,
  form: PropTypes.object.isRequired,
  fieldErrors: PropTypes.array,
  onErrors: PropTypes.func
};

PreparedCard.propTypes = {
  form: PropTypes.object.isRequired,
  fieldErrors: PropTypes.array,
  templateName: PropTypes.string,
  t: PropTypes.func.isRequired
};

EeReviewBody.propTypes = {
  papId: PropTypes.number,
  patientId: PropTypes.number,
  /** antd form instance owned by the modal, so the footer can read/validate it. */
  form: PropTypes.object.isRequired,
  /** Called with the preview once loaded — the modal needs `papId`/template on approve. */
  onReady: PropTypes.func,
  /** Field-error reporter the shared ApplicationForm calls; gates Approve. */
  onErrors: PropTypes.func,
  /** The run's flow summary, built by the modal (which owns the run-audit fetch). */
  summary: PropTypes.node,
  /** How many stages that summary shows — surfaced while it's collapsed. */
  summaryStepCount: PropTypes.number
};

export default EeReviewBody;
